// previous version:
// https://github.com/angular-ui/bootstrap/blob/07c31d0731f7cb068a1932b8e01d2312b796b4ec/src/position/position.js
export class Positioning {
  private getStyle(element: HTMLElement, prop: string): string { return window.getComputedStyle(element)[prop]; }

  private isStaticPositioned(element: HTMLElement): boolean {
    return (this.getStyle(element, 'position') || 'static') === 'static';
  }

  private offsetParent(element: HTMLElement): HTMLElement {
    let offsetParentEl = <HTMLElement>element.offsetParent || document.documentElement;

    while (offsetParentEl && offsetParentEl !== document.documentElement && this.isStaticPositioned(offsetParentEl)) {
      offsetParentEl = <HTMLElement>offsetParentEl.offsetParent;
    }

    return offsetParentEl || document.documentElement;
  }

  position(element: HTMLElement, round = true): ClientRect {
    let elPosition: ClientRect;
    let parentOffset: ClientRect = {
      width: 0,
      height: 0,
      top: 0,
      bottom: 0,
      left: 0,
      right: 0,
      x: 0,
      y: 0,
      toJSON: () => ({ width: 0, height: 0, top: 0, bottom: 0, left: 0, right: 0, x: 0, y: 0 })
    }; // إضافة x و y لتلبية واجهة ClientRect

    if (this.getStyle(element, 'position') === 'fixed') {
      elPosition = element.getBoundingClientRect();
    } else {
      const offsetParentEl = this.offsetParent(element);

      elPosition = this.offset(element, false);

      if (offsetParentEl !== document.documentElement) {
        parentOffset = this.offset(offsetParentEl, false);
      }

      parentOffset = {
        ...parentOffset,
        top: parentOffset.top + offsetParentEl.clientTop,
        left: parentOffset.left + offsetParentEl.clientLeft
      };
    }

    // إنشاء نسخة جديدة من ClientRect وتعديل القيم المطلوبة
    let adjustedPosition: ClientRect = {
      top: elPosition.top - parentOffset.top,
      bottom: elPosition.bottom - parentOffset.top,
      left: elPosition.left - parentOffset.left,
      right: elPosition.right - parentOffset.left,
      width: elPosition.width,  // احتفظ بالقيم الأصلية
      height: elPosition.height,  // احتفظ بالقيم الأصلية
      x: elPosition.x - parentOffset.left,  // إضافة x
      y: elPosition.y - parentOffset.top,  // إضافة y
      toJSON: () => ({ top: elPosition.top - parentOffset.top, bottom: elPosition.bottom - parentOffset.top, left: elPosition.left - parentOffset.left, right: elPosition.right - parentOffset.left, width: elPosition.width, height: elPosition.height, x: elPosition.x - parentOffset.left, y: elPosition.y - parentOffset.top })
    };

    if (round) {
      adjustedPosition = {
        ...adjustedPosition,
        top: Math.round(adjustedPosition.top),
        bottom: Math.round(adjustedPosition.bottom),
        left: Math.round(adjustedPosition.left),
        right: Math.round(adjustedPosition.right),
        x: Math.round(adjustedPosition.x),
        y: Math.round(adjustedPosition.y)
      };
    }

    return adjustedPosition;
  }


  offset(element: HTMLElement, round = true): ClientRect {
    const elBcr = element.getBoundingClientRect();
    const viewportOffset = {
      top: window.pageYOffset - document.documentElement.clientTop,
      left: window.pageXOffset - document.documentElement.clientLeft
    };

    let elOffset: ClientRect = {
      height: elBcr.height || element.offsetHeight,
      width: elBcr.width || element.offsetWidth,
      top: elBcr.top + viewportOffset.top,
      bottom: elBcr.bottom + viewportOffset.top,
      left: elBcr.left + viewportOffset.left,
      right: elBcr.right + viewportOffset.left,
      x: elBcr.left + viewportOffset.left,
      y: elBcr.top + viewportOffset.top,
      toJSON: () => ({ height: elBcr.height || element.offsetHeight, width: elBcr.width || element.offsetWidth, top: elBcr.top + viewportOffset.top, bottom: elBcr.bottom + viewportOffset.top, left: elBcr.left + viewportOffset.left, right: elBcr.right + viewportOffset.left, x: elBcr.left + viewportOffset.left, y: elBcr.top + viewportOffset.top })
    };

    if (round) {
      elOffset = {
        ...elOffset,
        height: Math.round(elOffset.height),
        width: Math.round(elOffset.width),
        top: Math.round(elOffset.top),
        bottom: Math.round(elOffset.bottom),
        left: Math.round(elOffset.left),
        right: Math.round(elOffset.right)
      };
    }

    return elOffset;
  }

  positionElements(hostElement: HTMLElement, targetElement: HTMLElement, placement: string, appendToBody?: boolean):
    ClientRect {
    const hostElPosition = appendToBody ? this.offset(hostElement, false) : this.position(hostElement, false);
    const shiftWidth: any = {
      left: hostElPosition.left,
      center: hostElPosition.left + hostElPosition.width / 2 - targetElement.offsetWidth / 2,
      right: hostElPosition.left + hostElPosition.width
    };
    const shiftHeight: any = {
      top: hostElPosition.top,
      center: hostElPosition.top + hostElPosition.height / 2 - targetElement.offsetHeight / 2,
      bottom: hostElPosition.top + hostElPosition.height
    };
    const targetElBCR = targetElement.getBoundingClientRect();
    const placementPrimary = placement.split('-')[0] || 'top';
    const placementSecondary = placement.split('-')[1] || 'center';

    let targetElPosition = {
      height: targetElBCR.height || targetElement.offsetHeight,
      width: targetElBCR.width || targetElement.offsetWidth,
      top: 0,
      bottom: targetElBCR.height || targetElement.offsetHeight,
      left: 0,
      right: targetElBCR.width || targetElement.offsetWidth,
      x: 0,
      y: 0,
      toJSON: () => ({ height: targetElBCR.height || targetElement.offsetHeight, width: targetElBCR.width || targetElement.offsetWidth, top: 0, bottom: targetElBCR.height || targetElement.offsetHeight, left: 0, right: targetElBCR.width || targetElement.offsetWidth, x: 0, y: 0 })
    };

    switch (placementPrimary) {
      case 'top':
        targetElPosition = {
          ...targetElPosition,
          top: hostElPosition.top - targetElement.offsetHeight,
          bottom: targetElPosition.bottom + hostElPosition.top - targetElement.offsetHeight,
          left: shiftWidth[placementSecondary],
          right: targetElPosition.right + shiftWidth[placementSecondary]
        };
        break;
      case 'bottom':
        targetElPosition = {
          ...targetElPosition,
          top: shiftHeight[placementPrimary],
          bottom: targetElPosition.bottom + shiftHeight[placementPrimary],
          left: shiftWidth[placementSecondary],
          right: targetElPosition.right + shiftWidth[placementSecondary]
        };
        break;
      case 'left':
        targetElPosition = {
          ...targetElPosition,
          top: shiftHeight[placementSecondary],
          bottom: targetElPosition.bottom + shiftHeight[placementSecondary],
          left: hostElPosition.left - targetElement.offsetWidth,
          right: targetElPosition.right + hostElPosition.left - targetElement.offsetWidth
        };
        break;
      case 'right':
        targetElPosition = {
          ...targetElPosition,
          top: shiftHeight[placementSecondary],
          bottom: targetElPosition.bottom + shiftHeight[placementSecondary],
          left: shiftWidth[placementPrimary],
          right: targetElPosition.right + shiftWidth[placementPrimary]
        };
        break;
    }

    targetElPosition = {
      ...targetElPosition,
      top: Math.round(targetElPosition.top),
      bottom: Math.round(targetElPosition.bottom),
      left: Math.round(targetElPosition.left),
      right: Math.round(targetElPosition.right)
    };

    return targetElPosition;
  }
}

const positionService = new Positioning();
export function positionElements(
  hostElement: HTMLElement, targetElement: HTMLElement, placement: string, appendToBody?: boolean): void {
  const pos = positionService.positionElements(hostElement, targetElement, placement, appendToBody);

  targetElement.style.top = `${pos.top}px`;
  targetElement.style.left = `${pos.left}px`;
}
